import React from 'react';

interface ConnectionPreviewProps {
  n: number;
  s: number;
  ed1: number;
  ed2: number;
}

const ConnectionPreview: React.FC<ConnectionPreviewProps> = ({ n, s, ed1, ed2 }) => {
  // SVG Canvas
  const width = 400;
  const height = 400;
  
  // Center
  const cx = width / 2;
  const cy = height / 2;
  
  // Scales
  const scale = 30; // pixels per inch approx
  
  // Bracket visualization (Front view)
  const bWidth = 100; // Fixed visual width for the plate face
  // Calculate height based on inputs
  // Height = ED1 + (N-1)*S + ED2
  const calcHeight = ed1 + (Math.max(0, n - 1) * s) + ed2;
  // Limit visual height to keep inside box
  const visualHeight = Math.min(300, Math.max(100, calcHeight * 20)); // scaling factor for visual
  
  // Map real Y positions to visual Y positions
  const startY = cy - visualHeight / 2;
  
  // Calculate relative positions for holes
  // We distribute them proportionally on the visual representation
  const getHoleY = (index: number) => {
      if (calcHeight <= 0) return cy;
      // Real Y from top
      const realY = ed1 + index * s;
      const ratio = realY / calcHeight;
      return startY + ratio * visualHeight;
  };

  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
      <div className="relative w-full h-full flex items-center justify-center">
        <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
            
            {/* --- Main Diagram --- */}
            <g transform="translate(0, 0)">
                
                {/* 1. Bracket Plate (Front View) */}
                <rect 
                    x={cx - bWidth/2} 
                    y={startY} 
                    width={bWidth} 
                    height={visualHeight} 
                    fill="none" 
                    stroke="#34495E" 
                    strokeWidth="2" 
                />
                
                {/* 2. Holes */}
                {Array.from({ length: n }).map((_, i) => (
                    <g key={i}>
                        <circle 
                            cx={cx} 
                            cy={getHoleY(i)} 
                            r={6} 
                            fill="white" 
                            stroke="#34495E" 
                            strokeWidth="1.5" 
                        />
                        {/* Crosshair */}
                        <line x1={cx-8} y1={getHoleY(i)} x2={cx+8} y2={getHoleY(i)} stroke="#34495E" strokeWidth="0.5" />
                        <line x1={cx} y1={getHoleY(i)-8} x2={cx} y2={getHoleY(i)+8} stroke="#34495E" strokeWidth="0.5" />
                    </g>
                ))}

                {/* 3. Side View (Right side) - Simple L shape */}
                <g transform={`translate(${bWidth + 40}, 0)`}>
                    {/* Vertical Leg */}
                    <rect 
                        x={cx - 10} 
                        y={startY} 
                        width={6} 
                        height={visualHeight} 
                        fill="#E5E7EB" 
                        stroke="#34495E" 
                        strokeWidth="2" 
                    />
                    {/* Horizontal Leg */}
                    <rect 
                        x={cx - 10} 
                        y={startY + visualHeight - 6} 
                        width={30} 
                        height={6} 
                        fill="#E5E7EB" 
                        stroke="#34495E" 
                        strokeWidth="2" 
                    />
                     {/* Bolts/Pins in side view */}
                     {Array.from({ length: n }).map((_, i) => (
                        <line 
                            key={`side-${i}`}
                            x1={cx - 20} 
                            y1={getHoleY(i)} 
                            x2={cx + 5} 
                            y2={getHoleY(i)} 
                            stroke="#34495E" 
                            strokeWidth="1" 
                            strokeDasharray="2,2"
                        />
                     ))}
                     
                     <text x={cx + 5} y={startY + visualHeight + 20} textAnchor="middle" className="text-xs font-bold fill-gray-600">A-A</text>
                </g>

                {/* --- Dimensions --- */}
                
                {/* ED1 (Top Edge to First Hole) */}
                {n > 0 && (
                    <g>
                        <line x1={cx - bWidth/2 - 10} y1={startY} x2={cx - bWidth/2 - 30} y2={startY} stroke="#397D77" />
                        <line x1={cx - bWidth/2 - 10} y1={getHoleY(0)} x2={cx - bWidth/2 - 30} y2={getHoleY(0)} stroke="#397D77" />
                        <line x1={cx - bWidth/2 - 20} y1={startY} x2={cx - bWidth/2 - 20} y2={getHoleY(0)} stroke="#397D77" markerStart="url(#arrow)" markerEnd="url(#arrow)" />
                        <text x={cx - bWidth/2 - 35} y={startY + (getHoleY(0)-startY)/2} fill="#397D77" textAnchor="end" dominantBaseline="middle" className="text-xs font-bold">ED</text>
                    </g>
                )}

                {/* Spacing (First to Second, etc) */}
                {n > 1 && (
                    <g>
                        <line x1={cx - bWidth/2 - 10} y1={getHoleY(1)} x2={cx - bWidth/2 - 30} y2={getHoleY(1)} stroke="#397D77" />
                        <line x1={cx - bWidth/2 - 20} y1={getHoleY(0)} x2={cx - bWidth/2 - 20} y2={getHoleY(1)} stroke="#397D77" markerStart="url(#arrow)" markerEnd="url(#arrow)" />
                        <text x={cx - bWidth/2 - 35} y={getHoleY(0) + (getHoleY(1)-getHoleY(0))/2} fill="#397D77" textAnchor="end" dominantBaseline="middle" className="text-xs font-bold">S</text>
                    </g>
                )}

                {/* ED2 (Last Hole to Bottom) */}
                {n > 0 && (
                     <g>
                        <line x1={cx - bWidth/2 - 10} y1={startY + visualHeight} x2={cx - bWidth/2 - 30} y2={startY + visualHeight} stroke="#397D77" />
                        <line x1={cx - bWidth/2 - 20} y1={getHoleY(n-1)} x2={cx - bWidth/2 - 20} y2={startY + visualHeight} stroke="#397D77" markerStart="url(#arrow)" markerEnd="url(#arrow)" />
                        <text x={cx - bWidth/2 - 35} y={getHoleY(n-1) + (startY + visualHeight - getHoleY(n-1))/2} fill="#397D77" textAnchor="end" dominantBaseline="middle" className="text-xs font-bold">ED</text>
                    </g>
                )}
                
                {/* Horizontal Dimension (Edge to Hole CL) - ED3? */}
                <g>
                     <line x1={cx} y1={startY - 10} x2={cx} y2={startY - 30} stroke="#397D77" />
                     <line x1={cx + bWidth/2} y1={startY - 10} x2={cx + bWidth/2} y2={startY - 30} stroke="#397D77" />
                     <line x1={cx} y1={startY - 20} x2={cx + bWidth/2} y2={startY - 20} stroke="#397D77" markerStart="url(#arrow)" markerEnd="url(#arrow)" />
                     <text x={cx + bWidth/4} y={startY - 35} fill="#397D77" textAnchor="middle" className="text-xs font-bold">ED</text>
                </g>

            </g>
            
            <defs>
                <marker id="arrow" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                    <path d="M 0 0 L 6 3 L 0 6 z" fill="#397D77" />
                </marker>
            </defs>

        </svg>
        
        {/* Footnote */}
        <div className="absolute bottom-4 left-0 right-0 px-8 text-center">
             <p className="text-[10px] text-gray-500 uppercase leading-tight font-medium">
                 NOTE: THE ED, S, ED... DIMENSIONS ARE FROM THE BOTTOM OR TOP BOLT TO THE BRACKET EDGE (IGNORE OPEN HOLES)
             </p>
        </div>
      </div>
    </div>
  );
};

export default ConnectionPreview;
